import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from "firebase/firestore";
import Spinner from './Spinner.js';
import { Paperclip, FileText, Eye, Calendar, X, ChevronDown, ChevronUp } from 'lucide-react';
import { isOpsManager } from './accessControl.js';
import { updateOrderWithAudit } from './utils/orderAudit.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';

const OrderPreview = ({ order, customer, firms, db, user }) => {
  const [localOrder, setLocalOrder] = useState(order);
  const [orderAuditHistory, setOrderAuditHistory] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDateOption, setScheduleDateOption] = useState('today');
  const [customScheduleDate, setCustomScheduleDate] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [scheduleRemarks, setScheduleRemarks] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isScheduleHistoryOpen, setIsScheduleHistoryOpen] = useState(false);

  useEffect(() => {
    setLocalOrder(order);
  }, [order]);

  useEffect(() => {
    if (!db || !order?.id) return;

    const auditRef = doc(db, "audit_events", order.id);
    const unsubscribe = onSnapshot(auditRef, (snapshot) => {
      if (snapshot.exists()) {
        setOrderAuditHistory({ id: snapshot.id, ...snapshot.data() });
      } else {
        setOrderAuditHistory(null);
      }
    });

    return () => unsubscribe();
  }, [db, order?.id]);

  useEffect(() => {
    if (scheduleDateOption === 'no_schedule') {
      setScheduleNote('');
      setScheduleRemarks('');
    }
  }, [scheduleDateOption]);

  const currentOrder = localOrder || order;
  const isOpsScheduler = !!db && (user?.role === 'ops' || isOpsManager(user));

  if (!currentOrder || !customer || !firms.length) {
    return <div style={{padding: '20px', textAlign: 'center'}}><Spinner /></div>;
  }
  
  const selectedFirm = firms.find(f => f.id === currentOrder.firmId);
  if (!selectedFirm) return <p>Firm details not found.</p>;

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
        const decodedUrl = decodeURIComponent(url);
        const pathParts = decodedUrl.split('?')[0].split('/');
        const fileNameWithPrefix = pathParts[pathParts.length - 1];
        const underscoreIndex = fileNameWithPrefix.indexOf('_');
        if (underscoreIndex !== -1) {
            if (/^\d{13,}/.test(fileNameWithPrefix)) {
                return fileNameWithPrefix.substring(underscoreIndex + 1);
            }
            const lastUnderscoreIndex = fileNameWithPrefix.lastIndexOf('_');
            if (lastUnderscoreIndex !== -1 && /\d{13,}/.test(fileNameWithPrefix.substring(lastUnderscoreIndex + 1))) {
                return fileNameWithPrefix.substring(0, lastUnderscoreIndex);
            }
        }
        return fileNameWithPrefix;
    } catch (e) {
        return 'Attached File';
    }
  };

  const formattedOrderDate = new Date(currentOrder.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const formattedQuoteDate = currentOrder.quoteDate ? new Date(currentOrder.quoteDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formattedWoDate = currentOrder.woDate ? new Date(currentOrder.woDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formattedPoDate = currentOrder.poDate ? new Date(currentOrder.poDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formatAttachmentDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const claimedTransportCharges = Number(currentOrder.claimedChargesAsPerPoOrder);
  const claimedTransportChargesDisplay = Number.isFinite(claimedTransportCharges)
    ? `Rs.${claimedTransportCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : (currentOrder.claimedChargesAsPerPoOrder ?? '---');

  const formatDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getScheduleDateFromOption = () => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);

    if (scheduleDateOption === 'no_schedule') return '';
    if (scheduleDateOption === 'today') return formatDateInputValue(baseDate);
    if (scheduleDateOption === 'tomorrow') {
      const tomorrow = new Date(baseDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateInputValue(tomorrow);
    }
    if (scheduleDateOption === 'in_2_days') {
      const inTwoDays = new Date(baseDate);
      inTwoDays.setDate(inTwoDays.getDate() + 2);
      return formatDateInputValue(inTwoDays);
    }
    return customScheduleDate;
  };

  const getPresetScheduleDisplayDate = (optionId) => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);
    if (optionId === 'tomorrow') baseDate.setDate(baseDate.getDate() + 1);
    if (optionId === 'in_2_days') baseDate.setDate(baseDate.getDate() + 2);
    return formatDate(formatDateInputValue(baseDate));
  };

  const getScheduleOptionForDate = (dateString) => {
    if (!dateString) return 'no_schedule';
    if (dateString === formatDateInputValue(new Date())) return 'today';

    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateString === formatDateInputValue(tomorrow)) return 'tomorrow';

    const inTwoDays = new Date();
    inTwoDays.setHours(0, 0, 0, 0);
    inTwoDays.setDate(inTwoDays.getDate() + 2);
    if (dateString === formatDateInputValue(inTwoDays)) return 'in_2_days';

    return 'custom';
  };

  const buildScheduleHistoryEntry = ({ action, date, note, remarks, timestamp }) => ({
    action,
    date: date || '',
    note: note || '',
    remarks: remarks || '',
    updatedAt: timestamp,
    updatedBy: user?.email || user?.name || 'Unknown'
  });

  const openScheduleModal = () => {
    const todayValue = formatDateInputValue(new Date());
    setScheduleDateOption(getScheduleOptionForDate(currentOrder.dispatchScheduleDate));
    setCustomScheduleDate(currentOrder.dispatchScheduleDate || todayValue);
    setScheduleNote(currentOrder.dispatchScheduleNote || 'Tentative');
    setScheduleRemarks(currentOrder.dispatchScheduleRemarks || '');
    setIsScheduleHistoryOpen(false);
    setShowScheduleModal(true);
  };

  const resetScheduleModal = () => {
    setShowScheduleModal(false);
    setScheduleDateOption('today');
    setCustomScheduleDate('');
    setScheduleNote('');
    setScheduleRemarks('');
    setIsScheduleHistoryOpen(false);
  };

  const handleScheduleSave = async () => {
    if (!db || !currentOrder?.id) return;

    const selectedDate = getScheduleDateFromOption();
    const isNoSchedule = scheduleDateOption === 'no_schedule';
    if (!isNoSchedule && !scheduleNote) {
      alert("Please choose a schedule note.");
      return;
    }
    if (isNoSchedule && !scheduleRemarks.trim()) {
      alert("Please add Schedule Remarks before saving No Schedule.");
      return;
    }

    setIsSavingSchedule(true);
    try {
      const timestamp = new Date().toISOString();
      const nextScheduleNote = isNoSchedule ? '' : scheduleNote;
      const nextScheduleRemarks = scheduleRemarks.trim();
      const nextHistory = [
        ...(currentOrder.dispatchScheduleHistory || []),
        buildScheduleHistoryEntry({
          action: isNoSchedule ? 'reset' : 'saved',
          date: selectedDate,
          note: nextScheduleNote,
          remarks: nextScheduleRemarks,
          timestamp
        })
      ];
      const payload = {
        dispatchScheduleDate: selectedDate || '',
        dispatchScheduleNote: nextScheduleNote,
        dispatchScheduleRemarks: nextScheduleRemarks,
        dispatchScheduleHistory: nextHistory,
        dispatchScheduleUpdatedAt: timestamp,
        updatedAt: timestamp
      };

      if (!currentOrder.dispatchScheduledAt) payload.dispatchScheduledAt = timestamp;
      if (user?.email) payload.dispatchScheduledBy = user.email;

      await updateOrderWithAudit(db, {
        orderId: currentOrder.id,
        orderPayload: {
          ...payload,
          updatedBy: user?.displayName || user?.email || 'Unknown',
          updatedByEmail: user?.email || ''
        },
        previousData: currentOrder,
        user,
        historyEvent: {
          action: 'schedule_set',
          status: currentOrder.status,
          user,
          timestamp,
          remarks: nextScheduleRemarks
        },
        auditFields: {
          scheduleSetAt: timestamp,
          scheduleSetBy: user?.displayName || user?.email || 'Unknown'
        }
      });
      setLocalOrder(prev => ({ ...prev, ...payload }));
      resetScheduleModal();
    } catch (error) {
      alert("Failed to save dispatch schedule.");
    } finally {
      setIsSavingSchedule(false);
    }
  };

  // CSS exactly matched from OrderDetail.js handleExportPdf
  const containerStyle = { width: '100%', maxWidth: '1000px', margin: '0 auto', fontFamily: 'Arial, sans-serif', color: '#1b1b1b', backgroundColor: 'white', boxSizing: 'border-box' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };
  const headerTableStyle = { ...tableStyle, border: '1px solid #1b1b1b' };
  const headerTableCellStyle = { padding: '10px', verticalAlign: 'middle', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' };
  const upperTableTdStyle = { padding: '8px', textAlign: 'left', border: '1px solid #1b1b1b', fontSize: '16px', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' };
  const detailLabelCellStyle = { ...upperTableTdStyle, width: '18.75%' };
  const detailValueCellStyle = { ...upperTableTdStyle, width: '31.25%' };
  const tableTitleStyle = { backgroundColor: '#f2f2f2', fontWeight: 'bold' };
  const itemTableThStyle = { border: '1px solid #1b1b1b', padding: '8px', verticalAlign: 'top', backgroundColor: '#f9f9f9', textAlign: 'left', fontSize: '16px', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' };
  const itemTableTdStyle = { border: '1px solid #1b1b1b', padding: '8px', verticalAlign: 'top', fontSize: '16px', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' };
  const summaryTableStyle = { ...tableStyle, border: '1px solid #1b1b1b', marginTop: '20px' };
  const summaryTableTdStyle = { padding: '12px', fontSize: '16px', border: '1px solid #1b1b1b', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' };

  // Attachment Section Styles
  const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '30px', border: '1px solid #e5e7eb' };
  const attachmentCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.2s'
  };
  const attachmentIconStyle = (bg = '#eff6ff', color = '#2563eb') => ({
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  });
  const attachmentLabelStyle = { fontSize: '13px', fontWeight: '600', color: '#374151' };
  const attachmentNameStyle = { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' };

  return (
    <div style={containerStyle}>
      {isOpsScheduler && (
        <div
          className="no-print"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginBottom: '16px'
          }}
        >
          <button
            onClick={openScheduleModal}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#1f2937',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            <Calendar size={16} /> Schedule
          </button>
        </div>
      )}

      {/* Header Table */}
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, borderRight: '1px solid #1b1b1b', width: '20%' }}>
              <img width="150px" src={selectedFirm.logo} alt="Logo" />
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '14px' }}>
              <strong>{selectedFirm.name}</strong>,<br />
              {selectedFirm.address}<br />
              GSTIN - {selectedFirm.gstin}<br />
              E-Mail - {selectedFirm.email}<br />
              Tel: {selectedFirm.tel}
            </td>
            <td style={{ ...headerTableCellStyle, textAlign: 'right', verticalAlign: 'top' }}>
              <p style={{ fontSize: '25px', fontWeight: 'bold', margin: 0 }}>{currentOrder.orderType.toUpperCase()} ORDER</p>
              {currentOrder.orderNumber || currentOrder.id}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Upper Table */}
      <table style={{ ...tableStyle, borderCollapse: 'collapse' }}>
        <tbody>
          <tr style={tableTitleStyle}>
            <td colSpan="4" style={upperTableTdStyle}><strong>Order Details:</strong></td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Date:</strong></td><td style={detailValueCellStyle}>{formattedOrderDate}</td>
            <td style={detailLabelCellStyle}><strong>Quote Number:</strong></td><td style={detailValueCellStyle}>{currentOrder.quoteNumber || "N/A"}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Order ID:</strong></td><td style={detailValueCellStyle}>{currentOrder.orderNumber || currentOrder.id}</td>
            <td style={detailLabelCellStyle}><strong>Quote Date:</strong></td><td style={detailValueCellStyle}>{formattedQuoteDate}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Salesperson:</strong></td><td style={detailValueCellStyle}>{currentOrder.salesPerson}</td>
            <td style={detailLabelCellStyle}><strong>{currentOrder.orderType === 'Sales' ? 'PO Number' : 'Work Order Number'}:</strong></td><td style={detailValueCellStyle}>{currentOrder.orderType === 'Sales' ? currentOrder.poNumber : currentOrder.workOrderNumber || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Project Name:</strong></td><td style={detailValueCellStyle}>{currentOrder.projectName || ''}</td>
            <td style={detailLabelCellStyle}><strong>{currentOrder.orderType === 'Sales' ? 'PO Date' : 'Work Order Date'}:</strong></td><td style={detailValueCellStyle}>{currentOrder.orderType === 'Sales' ? formattedPoDate : formattedWoDate || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Project Location:</strong></td><td style={detailValueCellStyle}>{currentOrder.projectLocation?.address || ''}</td>
            <td style={detailLabelCellStyle}><strong>{currentOrder.orderType === 'Sales' ? 'Payment Terms:' : 'Rental Tenure:'}</strong></td>
            <td style={detailValueCellStyle}>
              {currentOrder.orderType === 'Sales' ? (customer?.payment_terms || 'N/A') : (currentOrder.rentalTenureMonths + ' Months')}
            </td>
          </tr>
          {currentOrder.orderType === 'Rental' && (
            <>
              <tr>
                <td style={detailLabelCellStyle}><strong>Rental Agreement:</strong></td><td style={detailValueCellStyle}>{currentOrder.rentalAgreementReceived ? 'Received' : 'Pending'}</td>
                <td style={detailLabelCellStyle}><strong>Payment Terms:</strong></td><td style={detailValueCellStyle}>{customer?.payment_terms || 'N/A'}</td>
              </tr>
              <tr>
                <td style={detailLabelCellStyle}><strong>Security Cheque:</strong></td><td style={detailValueCellStyle}>{currentOrder.securityChequeReceived ? 'Received' : 'Pending'}</td>
                <td style={detailLabelCellStyle}><strong>Background Verif:</strong></td><td style={detailValueCellStyle}>{currentOrder.backgroundVerificationDone ? `Done (${currentOrder.backgroundVerificationDoneBy})` : 'Pending'}</td>
              </tr>
            </>
          )}
          
          <tr style={tableTitleStyle}>
            <td style={{ ...upperTableTdStyle, width: '33%' }}><strong>Logistics Details</strong></td>
            <td style={{ ...upperTableTdStyle, width: '33%' }}><strong>Installation Details</strong></td>
            <td style={{ ...upperTableTdStyle, width: '34%' }} colSpan="2"><strong>Material Checklist</strong></td>
          </tr>
          <tr>
            <td style={{ ...upperTableTdStyle, width: '33%', verticalAlign: 'top' }}>{currentOrder.logisticsDetails || 'N/A'}</td>
            <td style={{ ...upperTableTdStyle, width: '33%', verticalAlign: 'top' }}>{currentOrder.installationDetails || 'N/A'}</td>
            <td style={{ ...upperTableTdStyle, width: '34%', verticalAlign: 'top' }} colSpan="2">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Stickers: {currentOrder.materialChecklist?.stickers ? 'Yes' : 'No'}</li>
                <li>Laser Marking: {currentOrder.materialChecklist?.laserMarking ? 'Yes' : 'No'}</li>
                <li>Material Wrapping: {currentOrder.materialChecklist?.materialWrapping ? 'Yes' : 'No'}</li>
                {currentOrder.materialChecklist?.materialInstructions ? <li>Instructions: {currentOrder.materialChecklist.materialInstructions}</li> : ''}
              </ul>
            </td>
          </tr>
          <tr>
            <td colSpan="4" style={upperTableTdStyle}>
              <strong>Claimed Transport Charges:</strong> {claimedTransportChargesDisplay}
            </td>
          </tr>

          <tr style={tableTitleStyle}>
            <td colSpan="2" style={upperTableTdStyle}><strong>Billing Details:</strong></td>
            <td colSpan="2" style={upperTableTdStyle}><strong>Shipping Details:</strong></td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Company Name</strong></td><td style={detailValueCellStyle}>{customer.company_name || customer.display_name}</td>
            <td style={detailLabelCellStyle}><strong>Ship to:</strong></td><td style={detailValueCellStyle}>{currentOrder.shippingAddress?.attention || customer.display_name}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Address</strong></td><td style={detailValueCellStyle}>{formatAddress(currentOrder.billingAddress)}</td>
            <td style={detailLabelCellStyle}><strong>Address</strong></td><td style={detailValueCellStyle}>{formatAddress(currentOrder.shippingAddress)}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Person</strong></td><td style={detailValueCellStyle}>{currentOrder.billingAddress?.attention || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Person</strong></td><td style={detailValueCellStyle}>{currentOrder.shippingAddress?.attention || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Number</strong></td><td style={detailValueCellStyle}>{currentOrder.billingAddress?.phone || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Number</strong></td><td style={detailValueCellStyle}>{currentOrder.shippingAddress?.phone || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Email</strong></td><td style={detailValueCellStyle}>{currentOrder.billingAddress?.email || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Email</strong></td><td style={detailValueCellStyle}>{currentOrder.shippingAddress?.email || ''}</td>
          </tr>
        </tbody>
      </table>

      {/* Summary Table */}
      <table style={summaryTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...summaryTableTdStyle, fontWeight: 'bold', textAlign: 'right' }}>
              Total Order Value:<br />
              <strong>Rs.{(currentOrder.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </td>
            <td style={{ ...summaryTableTdStyle, fontWeight: 'bold', textAlign: 'right', width: '30%' }}>
              Payment Status:<br />
              {currentOrder.paymentStatus || "N/A"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* BOMs */}
      {currentOrder.boms.map((bom, idx) => (
        <div key={idx}>
          <h2 style={{ marginTop: '20px', marginBottom: '10px', textTransform: 'uppercase', fontWeight: 'bold' }}>{bom.name}</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...itemTableThStyle, width: '10%' }}>Sr. No.</th>
                <th style={{ ...itemTableThStyle, width: '18%' }}>SKU</th>
                <th style={{ ...itemTableThStyle, width: '44%' }}>Item</th>
                <th style={{ ...itemTableThStyle, width: '14%', textAlign: 'right' }}>Quantity Per Set</th>
                <th style={{ ...itemTableThStyle, width: '14%', textAlign: 'right' }}>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {bom.components.map((comp, index) => (
                <tr key={index}>
                  <td style={itemTableTdStyle}>{index + 1}</td>
                  <td style={itemTableTdStyle}>{comp.sku || "N/A"}</td>
                  <td style={itemTableTdStyle}>{comp.name}</td>
                  <td style={{ ...itemTableTdStyle, textAlign: 'right' }}>{comp.baseQty || 0}</td>
                  <td style={{ ...itemTableTdStyle, textAlign: 'right' }}>{Math.round(comp.baseQty * bom.multiplier)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
                <td colSpan="4" style={{ ...itemTableTdStyle, textAlign: 'right', padding: '8px' }}>Total Quantity:</td>
                <td style={{ ...itemTableTdStyle, textAlign: 'right', padding: '8px' }}>
                  {bom.components.reduce((sum, comp) => sum + Math.round((comp.baseQty || 0) * bom.multiplier), 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {/* --- ORDER ATTACHMENTS SECTION --- */}
      {(currentOrder.quoteFileUrl || currentOrder.poFileUrl || currentOrder.workOrderFileUrl || currentOrder.rentalAgreementFileUrl || currentOrder.securityChequeFileUrl) && (
        <div className="no-print" style={cardStyle}>
          <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontSize: '16px', fontWeight: 'bold', color: '#1e293b'}}>
            <Paperclip size={20} color="#2563eb"/> Order Attachments
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
            {currentOrder.quoteFileUrl && (
              <a href={currentOrder.quoteFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle()}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Quote Document</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(currentOrder.quoteFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {currentOrder.poFileUrl && (
              <a href={currentOrder.poFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#dcfce7', '#166534')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Purchase Order (PO)</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(currentOrder.poFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {currentOrder.workOrderFileUrl && (
              <a href={currentOrder.workOrderFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#fef9c3', '#854d0e')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Work Order</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(currentOrder.workOrderFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {currentOrder.rentalAgreementFileUrl && (
              <a href={currentOrder.rentalAgreementFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#ede9fe', '#5b21b6')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Rental Agreement</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(currentOrder.rentalAgreementFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {currentOrder.securityChequeFileUrl && (
              <a href={currentOrder.securityChequeFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#fee2e2', '#991b1b')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Security Cheque</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(currentOrder.securityChequeFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
          </div>
        </div>
      )}

      {currentOrder.additionalAttachments?.length > 0 && (
        <div className="no-print" style={cardStyle}>
          <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontSize: '16px', fontWeight: 'bold', color: '#1e293b'}}>
            <Paperclip size={20} color="#2563eb"/> Additional Attachments
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
            {currentOrder.additionalAttachments.map((file, index) => (
              <a key={`${file.url || file.name}-${index}`} href={file.url} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#e0f2fe', '#0369a1')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>{file.label || 'Additional Attachment'}</div>
                  <div style={attachmentNameStyle}>{file.name || getFileNameFromUrl(file.url)}</div>
                  {file.loggedAt && <div style={{...attachmentNameStyle, marginTop: '4px'}}>Logged: {formatAttachmentDateTime(file.loggedAt)}</div>}
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            ))}
          </div>
        </div>
      )}
      {showScheduleModal && (
        <div
          className="no-print"
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.35)', zIndex: 9998 }}
          onClick={resetScheduleModal}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '420px',
              maxWidth: '100%',
              height: '100%',
              backgroundColor: '#ffffff',
              boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.18)',
              padding: '24px 24px 120px 24px',
              overflowY: 'auto',
              boxSizing: 'border-box'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#111827' }}>Dispatch Schedule</h3>
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                  #{currentOrder.orderNumber || currentOrder.id}
                </p>
              </div>
              <button onClick={resetScheduleModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                Schedule Date
              </label>
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'tomorrow', label: 'Tomorrow' },
                  { id: 'in_2_days', label: 'In 2 Days' },
                  { id: 'custom', label: 'Custom' }
                ].map((option) => (
                  <label
                    key={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 14px',
                      border: scheduleDateOption === option.id ? '1px solid #60a5fa' : '1px solid #d1d5db',
                      borderRadius: '10px',
                      backgroundColor: scheduleDateOption === option.id ? '#eff6ff' : '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input type="radio" name="scheduleDateOption" value={option.id} checked={scheduleDateOption === option.id} onChange={(e) => setScheduleDateOption(e.target.value)} />
                      <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{option.label}</span>
                    </div>
                    {option.id !== 'custom' && option.id !== 'no_schedule' && (
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{getPresetScheduleDisplayDate(option.id)}</span>
                    )}
                  </label>
                ))}
              </div>
              {scheduleDateOption === 'custom' && (
                <div style={{ marginTop: '12px' }}>
                  <input
                    type="date"
                    value={customScheduleDate}
                    onChange={(e) => setCustomScheduleDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              )}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 14px',
                  border: scheduleDateOption === 'no_schedule' ? '1px solid #fca5a5' : '1px solid #fecaca',
                  borderRadius: '10px',
                  backgroundColor: scheduleDateOption === 'no_schedule' ? '#fee2e2' : '#fef2f2',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="radio" name="scheduleDateOption" value="no_schedule" checked={scheduleDateOption === 'no_schedule'} onChange={(e) => setScheduleDateOption(e.target.value)} />
                  <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>No Schedule</span>
                </div>
              </label>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Note
              </label>
              <select
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                disabled={scheduleDateOption === 'no_schedule'}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', backgroundColor: scheduleDateOption === 'no_schedule' ? '#f9fafb' : '#fff' }}
              >
                <option value="">Select note...</option>
                <option value="Stock expected">Stock expected</option>
                <option value="Tentative">Tentative</option>
                <option value="Client requested">Client requested</option>
                <option value="Transport planning">Transport planning</option>
                <option value="Material under preparation">Material under preparation</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Remarks
              </label>
              <textarea
                value={scheduleRemarks}
                onChange={(e) => setScheduleRemarks(e.target.value)}
                placeholder="Add any schedule context for ops and sales..."
                style={{ width: '100%', minHeight: '88px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', backgroundColor: '#fff', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={resetScheduleModal} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
              <button onClick={handleScheduleSave} disabled={isSavingSchedule} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#fff', cursor: isSavingSchedule ? 'not-allowed' : 'pointer', fontWeight: '700', opacity: isSavingSchedule ? 0.7 : 1 }}>
                {isSavingSchedule ? 'Saving...' : 'Save'}
              </button>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setIsScheduleHistoryOpen(prev => !prev)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e5e7eb', backgroundColor: '#f8fafc', cursor: 'pointer', color: '#111827', fontSize: '13px', fontWeight: '700' }}
              >
                <span>Schedule History</span>
                <span style={{ color: '#6b7280', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {isScheduleHistoryOpen ? 'Hide' : 'Show'}
                  {isScheduleHistoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              </button>
              {isScheduleHistoryOpen && (
                <div style={{ marginTop: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
                  {(currentOrder.dispatchScheduleHistory || []).length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280' }}>No schedule history yet.</div>
                  ) : (
                    [...(currentOrder.dispatchScheduleHistory || [])].slice().reverse().map((entry, index) => (
                      <div key={`${entry.updatedAt || 'history'}-${index}`} style={{ padding: '14px 16px', borderTop: index === 0 ? 'none' : '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: entry.action === 'reset' ? '#dc2626' : '#1d4ed8' }}>
                            {entry.action === 'reset' ? 'Reset to No Schedule' : 'Schedule Saved'}
                          </span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{formatDateTime(entry.updatedAt)}</span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#374151', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>Date: {entry.date ? formatDate(entry.date) : 'No Schedule'}</span>
                          <span>Note: {entry.note || '---'}</span>
                          <span>Remarks: {entry.remarks || '---'}</span>
                          <span>By: {entry.updatedBy || 'Unknown'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className="no-print"
        style={{
          marginTop: '40px',
          maxWidth: '1000px',
          marginInline: 'auto',
          animation: 'fadeIn 0.3s ease-in-out'
        }}
      >
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1e293b',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderBottom: '2px solid #f1f5f9',
          paddingBottom: '10px'
        }}>
          <Calendar size={20} color="#2563eb" /> Order Progress Trackers
        </h3>

        <HorizontalTimeline
          orderData={currentOrder}
          historyData={orderAuditHistory}
          title="Order Progress"
        />
      </div>

      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

export default OrderPreview;
